import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import type {
  IRegistrationLink,
  IRegistrationLinkEmptied,
  IRegistrationLinkPayload,
  IRegistrationLogoPayload,
  TRegistrationExperience,
} from '../entities/registration-link.model';
import type {
  IRegistrationCrmConfig,
  IRegistrationFormConfig,
  IRegistrationHealthConfig,
} from '../entities/registration-page.model';

/**
 * Authenticated surface of the registration Worker for the gym's registration
 * links, when it may have several.
 *
 * The tenant never travels in a request: the Worker derives it from the bearer,
 * and a link of another gym answers the same 404 as one that does not exist.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationLinkService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.registration_service;

  /** Every link of the gym, oldest first. A gym with none answers an empty list, not a 404. */
  public list(): Observable<IRegistrationLink[]> {
    return this.http
      .get<{ items: IRegistrationLink[] }>(`${this.baseUrl}/api/pages`, { withCredentials: true })
      .pipe(map(({ items }) => items));
  }

  /**
   * One link as it is now, its counts included. 404 for an id that is not the
   * gym's.
   */
  public get(linkId: string): Observable<IRegistrationLink> {
    return this.http.get<IRegistrationLink>(this.url(linkId), { withCredentials: true });
  }

  /** 409 when the address is taken, or when the gym is at its limit of links. */
  public create(payload: IRegistrationLinkPayload): Observable<IRegistrationLink> {
    return this.http.post<IRegistrationLink>(`${this.baseUrl}/api/pages`, payload, {
      withCredentials: true,
    });
  }

  /** New label, address and display name. 409 when another link has the address. */
  public update(linkId: string, payload: IRegistrationLinkPayload): Observable<IRegistrationLink> {
    return this.http.put<IRegistrationLink>(this.url(linkId), payload, { withCredentials: true });
  }

  /** Pauses or resumes. A paused link answers its visitors as an address that does not exist. */
  public setActive(linkId: string, isActive: boolean): Observable<IRegistrationLink> {
    return this.http.put<IRegistrationLink>(
      `${this.url(linkId)}/active`,
      { isActive },
      { withCredentials: true }
    );
  }

  /**
   * Empties the link: the handled registrations are deleted for good and the
   * ones still waiting go to the trash. Either way their CPFs can be sent
   * through the link again.
   */
  public empty(linkId: string): Observable<IRegistrationLinkEmptied> {
    return this.http.post<IRegistrationLinkEmptied>(`${this.url(linkId)}/empty`, null, {
      withCredentials: true,
    });
  }

  /**
   * Which optional fields the link's public form asks for. The body is the
   * whole form: the Worker refuses one with a field missing.
   */
  public saveForm(linkId: string, form: IRegistrationFormConfig): Observable<IRegistrationLink> {
    return this.http.put<IRegistrationLink>(`${this.url(linkId)}/form`, form, {
      withCredentials: true,
    });
  }

  /** Where a registration of this link goes in the CRM once the counter confirms it. */
  public saveCrm(linkId: string, crm: IRegistrationCrmConfig): Observable<IRegistrationLink> {
    return this.http.put<IRegistrationLink>(`${this.url(linkId)}/crm`, crm, {
      withCredentials: true,
    });
  }

  /** Whether the link's public form asks the health questionnaire. */
  public saveHealth(
    linkId: string,
    health: IRegistrationHealthConfig
  ): Observable<IRegistrationLink> {
    return this.http.put<IRegistrationLink>(`${this.url(linkId)}/health`, health, {
      withCredentials: true,
    });
  }

  /**
   * Whether the link opens the form in steps or the conversation. 400 for
   * anything that is not exactly one of the two.
   */
  public saveExperience(
    linkId: string,
    experience: TRegistrationExperience
  ): Observable<IRegistrationLink> {
    return this.http.put<IRegistrationLink>(
      `${this.url(linkId)}/experience`,
      { experience },
      { withCredentials: true }
    );
  }

  /**
   * The image at the top of the link's public page. `url` is the https address
   * of the uploaded image, and goes only with `custom`; 400 without it there,
   * and for a logo that is none of the four.
   */
  public saveLogo(linkId: string, logo: IRegistrationLogoPayload): Observable<IRegistrationLink> {
    return this.http.put<IRegistrationLink>(`${this.url(linkId)}/logo`, logo, {
      withCredentials: true,
    });
  }

  /**
   * Whether an address can be saved on a link. With `linkId`, the link's own
   * address counts as free, and a link that is not the gym's answers 404.
   *
   * With `null`, a link not created yet, no link travels and the Worker
   * measures against the gym's default link: that link's own address reads as
   * free, although creating a second link with it is refused.
   *
   * `reason` is Portuguese prose written by the Worker. The screen does not
   * print it: the same causes are told apart by the form's own validators, in
   * the user's language.
   */
  public checkSlug(
    slug: string,
    linkId: string | null
  ): Observable<{ available: boolean; reason?: string }> {
    const params = new HttpParams().set('slug', slug);

    return this.http.get<{ available: boolean; reason?: string }>(
      `${this.baseUrl}/api/slug/check`,
      {
        params: linkId === null ? params : params.set('pageId', linkId),
        withCredentials: true,
      }
    );
  }

  private url(linkId: string): string {
    return `${this.baseUrl}/api/pages/${encodeURIComponent(linkId)}`;
  }
}
